<template>
  <div class="content container-fluid">
    <breadcrumb-index :items="breadcrumbs" />

    <div class="row">
      <!-- Chat Sidebar -->
      <div class="col-lg-3">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="card-title mb-0">AI Assistant</h5>
            <span :class="['badge', aiStatus === 'online' ? 'bg-success' : 'bg-danger']">
              {{ aiStatus }}
            </span>
          </div>
          <div class="card-body">
            <p class="text-muted small mb-3">
              Ask questions about courses, generate content, or get help with exam preparation.
            </p>
            <button @click="clearChat" class="btn btn-outline-danger w-100" :disabled="!sessionId || loading">
              <vue-feather type="trash-2" size="16" class="me-2" />
              Clear Chat History
            </button>
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h5 class="card-title mb-0">Quick Actions</h5>
          </div>
          <div class="card-body">
            <div class="d-grid gap-2">
              <button @click="sendQuickAction('Generate 5 MCQ questions about')" class="btn btn-outline-primary btn-sm text-start">
                <vue-feather type="help-circle" size="14" class="me-2" />
                Generate Questions
              </button>
              <button @click="sendQuickAction('Summarize the following topic:')" class="btn btn-outline-primary btn-sm text-start">
                <vue-feather type="file-text" size="14" class="me-2" />
                Summarize Content
              </button>
              <button @click="sendQuickAction('Explain in simple terms:')" class="btn btn-outline-primary btn-sm text-start">
                <vue-feather type="book-open" size="14" class="me-2" />
                Explain Topic
              </button>
              <button @click="sendQuickAction('Create flashcards for:')" class="btn btn-outline-primary btn-sm text-start">
                <vue-feather type="layers" size="14" class="me-2" />
                Create Flashcards
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Chat Area -->
      <div class="col-lg-9">
        <div class="card chat-card">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <vue-feather type="message-circle" size="18" class="me-2" />
              TestPal AI Chat
            </h5>
          </div>
          <div class="card-body chat-body" ref="chatContainer">
            <!-- Welcome Message -->
            <div v-if="messages.length === 0" class="text-center py-5">
              <vue-feather type="cpu" size="48" class="text-muted mb-3" />
              <h5>Welcome to TestPal AI</h5>
              <p class="text-muted">Ask me anything about your courses, exams, or let me help you create content.</p>
            </div>

            <!-- Messages -->
            <div v-for="(msg, index) in messages" :key="index" :class="['message', msg.role]">
              <div class="message-avatar">
                <vue-feather :type="msg.role === 'user' ? 'user' : 'cpu'" size="18" />
              </div>
              <div class="message-content">
                <div 
                  class="message-text" 
                  :class="{ 'markdown-content': msg.role === 'assistant' }"
                  v-html="msg.role === 'assistant' ? renderMarkdown(msg.content) : escapeHtml(msg.content)"
                ></div>
                <div class="message-time">{{ formatTime(msg.timestamp) }}</div>
              </div>
              <button v-if="msg.role === 'assistant'" @click="copyMessage(msg.content)" class="btn btn-sm btn-link copy-btn">
                <vue-feather type="copy" size="14" />
              </button>
            </div>

            <!-- Typing Indicator -->
            <div v-if="loading" class="message assistant">
              <div class="message-avatar">
                <vue-feather type="cpu" size="18" />
              </div>
              <div class="message-content">
                <div class="typing-indicator">
                  <span></span><span></span><span></span>
                </div>
              </div>
            </div>
          </div>

          <!-- Input Area -->
          <div class="card-footer chat-footer">
            <form @submit.prevent="sendMessage" class="d-flex gap-2">
              <textarea
                v-model="inputMessage"
                class="form-control"
                rows="1"
                placeholder="Type your message..."
                @keydown.enter.exact.prevent="sendMessage"
                @input="autoResize"
                ref="inputField"
                :disabled="loading"
              ></textarea>
              <button type="submit" class="btn btn-primary" :disabled="loading || !inputMessage.trim()">
                <vue-feather type="send" size="18" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, nextTick } from 'vue'
import api from '@/services/api'
import { marked } from 'marked'
import katex from 'katex'
import 'katex/dist/katex.min.css'

export default {
  name: 'TestPalAIPage',
  setup() {
    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'TestPal AI', active: true }
    ]

    const messages = ref([])
    const inputMessage = ref('')
    const loading = ref(false)
    const sessionId = ref(null)
    const aiStatus = ref('checking')
    const chatContainer = ref(null)
    const inputField = ref(null)

    // Configure marked for safe rendering
    marked.setOptions({
      breaks: true,
      gfm: true
    })

    // Render LaTeX math expressions
    const renderMath = (text) => {
      // Handle display math $$...$$
      text = text.replace(/\$\$([\s\S]*?)\$\$/g, (match, math) => {
        try {
          return katex.renderToString(math.trim(), { displayMode: true, throwOnError: false })
        } catch (e) {
          return match
        }
      })
      
      // Handle inline math $...$
      text = text.replace(/\$([^$\n]+?)\$/g, (match, math) => {
        try {
          return katex.renderToString(math.trim(), { displayMode: false, throwOnError: false })
        } catch (e) {
          return match
        }
      })
      
      return text
    }

    // Render markdown with math support
    const renderMarkdown = (content) => {
      if (!content) return ''
      
      // First render math expressions
      let processed = renderMath(content)
      
      // Then render markdown
      processed = marked.parse(processed)
      
      return processed
    }

    // Escape HTML for user messages
    const escapeHtml = (text) => {
      const div = document.createElement('div')
      div.textContent = text
      return div.innerHTML.replace(/\n/g, '<br>')
    }

    const checkAIStatus = async () => {
      try {
        const response = await api.get('/v1/admin/admin-chat')
        aiStatus.value = response.data.status || 'online'
      } catch (error) {
        aiStatus.value = 'offline'
      }
    }

    const sendMessage = async () => {
      const content = inputMessage.value.trim()
      if (!content || loading.value) return

      // Add user message
      messages.value.push({
        role: 'user',
        content,
        timestamp: new Date()
      })
      inputMessage.value = ''
      scrollToBottom()

      loading.value = true
      try {
        const response = await api.post('/v1/admin/admin-chat', {
          message: content,
          session_id: sessionId.value
        })

        // Store session ID for conversation continuity
        if (response.data.session_id) {
          sessionId.value = response.data.session_id
        }

        // Add AI response
        messages.value.push({
          role: 'assistant',
          content: response.data.response,
          timestamp: new Date()
        })
      } catch (error) {
        const errorMsg = error.response?.data?.error?.message || 'Failed to get response. Please try again.'
        messages.value.push({
          role: 'assistant',
          content: errorMsg,
          timestamp: new Date(),
          isError: true
        })
      } finally {
        loading.value = false
        scrollToBottom()
      }
    }

    const sendQuickAction = (prompt) => {
      inputMessage.value = prompt + ' '
      inputField.value?.focus()
    }

    const clearChat = async () => {
      if (!sessionId.value) {
        messages.value = []
        return
      }

      try {
        await api.delete(`/v1/admin/admin-chat/${sessionId.value}`)
        messages.value = []
        sessionId.value = null
      } catch (error) {
        console.error('Failed to clear chat:', error)
      }
    }

    const copyMessage = (content) => {
      navigator.clipboard.writeText(content)
    }

    const formatTime = (date) => {
      return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }

    const scrollToBottom = () => {
      nextTick(() => {
        if (chatContainer.value) {
          chatContainer.value.scrollTop = chatContainer.value.scrollHeight
        }
      })
    }

    const autoResize = (e) => {
      const textarea = e.target
      textarea.style.height = 'auto'
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px'
    }

    onMounted(() => {
      checkAIStatus()
    })

    return {
      breadcrumbs,
      messages,
      inputMessage,
      loading,
      sessionId,
      aiStatus,
      chatContainer,
      inputField,
      sendMessage,
      sendQuickAction,
      clearChat,
      copyMessage,
      renderMarkdown,
      escapeHtml,
      formatTime,
      autoResize
    }
  }
}
</script>

<style scoped>
.chat-card {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 200px);
  min-height: 500px;
}

.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background: #f8f9fa;
}

.chat-footer {
  border-top: 1px solid #e9ecef;
  padding: 1rem;
  background: #fff;
}

.chat-footer textarea {
  resize: none;
  max-height: 120px;
}

.message {
  display: flex;
  gap: 12px;
  margin-bottom: 1rem;
  align-items: flex-start;
}

.message.user {
  flex-direction: row-reverse;
}

.message-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.message.user .message-avatar {
  background: #3d5ee1;
  color: #fff;
}

.message.assistant .message-avatar {
  background: #e9ecef;
  color: #495057;
}

.message-content {
  max-width: 80%;
}

.message-text {
  padding: 12px 16px;
  border-radius: 16px;
  line-height: 1.6;
}

.message.user .message-text {
  background: #3d5ee1;
  color: #fff;
  border-bottom-right-radius: 4px;
}

.message.assistant .message-text {
  background: #fff;
  color: #212529;
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.message-time {
  font-size: 11px;
  color: #6c757d;
  margin-top: 4px;
  padding: 0 8px;
}

.message.user .message-time {
  text-align: right;
}

.copy-btn {
  opacity: 0;
  transition: opacity 0.2s;
  padding: 4px;
}

.message:hover .copy-btn {
  opacity: 1;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 12px 16px;
  background: #fff;
  border-radius: 16px;
  border-bottom-left-radius: 4px;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #6c757d;
  border-radius: 50%;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  30% {
    transform: translateY(-4px);
    opacity: 1;
  }
}

/* Markdown Content Styles */
.markdown-content {
  font-size: 14px;
}

.markdown-content :deep(h1),
.markdown-content :deep(h2),
.markdown-content :deep(h3),
.markdown-content :deep(h4) {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  line-height: 1.3;
}

.markdown-content :deep(h3) {
  font-size: 1.1rem;
  color: #3d5ee1;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 0.5rem;
}

.markdown-content :deep(h4) {
  font-size: 1rem;
}

.markdown-content :deep(p) {
  margin-bottom: 0.75rem;
}

.markdown-content :deep(ul),
.markdown-content :deep(ol) {
  margin-bottom: 0.75rem;
  padding-left: 1.5rem;
}

.markdown-content :deep(li) {
  margin-bottom: 0.25rem;
}

.markdown-content :deep(strong) {
  font-weight: 600;
  color: #2c3e50;
}

.markdown-content :deep(em) {
  font-style: italic;
}

.markdown-content :deep(code) {
  background: #f1f3f4;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.9em;
  font-family: 'Monaco', 'Menlo', monospace;
}

.markdown-content :deep(pre) {
  background: #2d3748;
  color: #e2e8f0;
  padding: 1rem;
  border-radius: 8px;
  overflow-x: auto;
  margin: 0.75rem 0;
}

.markdown-content :deep(pre code) {
  background: none;
  padding: 0;
  color: inherit;
}

.markdown-content :deep(blockquote) {
  border-left: 4px solid #3d5ee1;
  padding-left: 1rem;
  margin: 0.75rem 0;
  color: #4a5568;
  font-style: italic;
}

.markdown-content :deep(hr) {
  border: none;
  border-top: 2px solid #e9ecef;
  margin: 1.5rem 0;
}

.markdown-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 0.75rem 0;
}

.markdown-content :deep(th),
.markdown-content :deep(td) {
  border: 1px solid #e9ecef;
  padding: 0.5rem;
  text-align: left;
}

.markdown-content :deep(th) {
  background: #f8f9fa;
  font-weight: 600;
}

/* KaTeX Math Styles */
.markdown-content :deep(.katex-display) {
  margin: 1rem 0;
  overflow-x: auto;
  overflow-y: hidden;
}

.markdown-content :deep(.katex) {
  font-size: 1.1em;
}

/* Question Card Styling */
.markdown-content :deep(h3:has(+ p > strong:first-child)) + p,
.markdown-content :deep(p:has(> strong:contains("Correct Answer"))) {
  background: #f0f9ff;
  padding: 0.5rem;
  border-radius: 4px;
}
</style>
